import type { AnyView, VersionString } from "@bestowinc/tinkertoy-core";
import { useEffect, useState } from "@bestowinc/tinkertoy-shell-deps/react";

import { useViewLoader } from "../view-loader";
import {
  getViewLoadPromise,
  peekLoadedView,
} from "../view-loader/view-read-resource";

import { useOptionalShellLoaderData } from "./use-shell-loader-data";

export type ShellViewLoadState =
  | { status: "pending" }
  | {
      status: "ready";
      view: AnyView;
      /** Canonical id of the view whose bundle produced `view`. */
      viewId: string;
      /** Version of the bundle that produced `view`. */
      viewVersion: VersionString;
    }
  | { status: "error"; error: unknown };

const loaderDataUnavailableError = new Error("Shell loader data is unavailable");

/**
 * Tracks the CDN view bundle for the current shell loader match. Shared by
 * {@link TinkertoyShell} and {@link DeveloperToolsHost}. Dev tools mount once
 * loading settles — success or failure — so overrides remain reachable on the
 * error boundary when a bundle cannot load.
 *
 * The shell loader awaits the view bundle before returning, so on the commit
 * that renders new loader data the bundle is normally already cached and the
 * state is `ready` synchronously — the shell never renders one view's
 * definition with another view's data. The effect path covers loader data
 * that didn't come through the shell loader (e.g. memory routers in tests).
 *
 * When loader data is missing (e.g. the route loader threw before returning),
 * this hook reports `error` so dev tools can still mount for override recovery.
 */
export function useShellViewLoadState(): ShellViewLoadState {
  const loaderData = useOptionalShellLoaderData();
  const viewLoader = useViewLoader();
  const viewId = loaderData?.loadedView.viewId;
  const viewVersion = loaderData?.loadedView.viewVersion;
  const key = `${viewId}@${viewVersion}`;
  const [loaded, setLoaded] = useState<{
    key: string;
    state: ShellViewLoadState;
  }>({ key: "", state: { status: "pending" } });

  const cached =
    viewId !== undefined && viewVersion !== undefined
      ? peekLoadedView(viewId, viewVersion)
      : undefined;

  useEffect(() => {
    if (viewId === undefined || viewVersion === undefined || cached) {
      return;
    }
    let cancelled = false;
    const loadKey = `${viewId}@${viewVersion}`;

    void getViewLoadPromise(viewLoader, viewId, viewVersion).then(
      result => {
        if (!cancelled) {
          setLoaded({
            key: loadKey,
            state: {
              status: "ready",
              view: result.definition,
              viewId,
              viewVersion,
            },
          });
        }
      },
      error => {
        if (!cancelled) {
          setLoaded({ key: loadKey, state: { status: "error", error } });
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [viewLoader, viewId, viewVersion, cached]);

  if (viewId === undefined || viewVersion === undefined) {
    return { status: "error", error: loaderDataUnavailableError };
  }
  if (cached) {
    return {
      status: "ready",
      view: cached.definition,
      viewId,
      viewVersion,
    };
  }
  return loaded.key === key ? loaded.state : { status: "pending" };
}
